/**
 * Sendeabsichten für den Plotter-Druck — eine Auftrags-ID samt Kopienzahl,
 * die bis zur Klärung stehen bleibt.
 *
 * Modulweit, nicht im Dialog: wer den Dialog während des Sendens oder nach
 * einem Fehler schließt und neu öffnet, sendet sonst mit neuer ID — der
 * Server legte einen zweiten Auftrag an, obwohl der erste vielleicht schon
 * angekommen ist. Die Absicht lebt so lange wie der Tab.
 *
 * Regeln:
 *  - Eine Absicht ist unveränderlich (ID, Kopien und Datei). Ein Wiederholen
 *    schickt genau sie noch einmal — auch aus einem später geöffneten Dialog
 *    mit inzwischen geändertem Plan; der Server antwortet idempotent mit dem
 *    Auftrag.
 *  - Erfolg löscht sie. Ein Fehler lässt sie stehen.
 *  - Taucht ihre ID in einer geladenen Liste auf, ist sie angekommen (auch
 *    wenn die Antwort verloren ging) und wird gelöscht.
 *  - Fehlt die ID in der Liste, heißt das NICHTS — die Liste zeigt nur die
 *    neuesten Aufträge. Verwerfen erst, wenn die gezielte Abfrage nach der ID
 *    (`plotAuftragFinden`), begonnen NACH dem Fehler, keinen Auftrag findet.
 *    Scheitert die Abfrage, bleibt Verwerfen gesperrt; beim nächsten Laden
 *    wird erneut geprüft.
 *
 * Schlüssel: `senden:<Dokument-Schlüssel>` bzw. `nachdruck:<alte Auftrags-ID>`.
 * Der Dokument-Schlüssel ist `PlotDokument.schluessel` (plotTypen.ts) — beim
 * SiGe-Plan die `sigeProjektId` wie bisher, bei der Vorankündigung
 * `vorank:<projects.id>`.
 */

export interface PlotSendeabsicht {
    readonly id: string;
    readonly kopien: number;
    /**
     * Dokument (`PlotDokument.schluessel`: SiGe-Plan oder Vorankündigung), in
     * dessen Auftragsliste der Auftrag erscheint. Der Name stammt aus der Zeit,
     * als nur der SiGe-Plan geplottet wurde.
     */
    readonly plan: string;
    /** Ein Aufruf mit dieser Absicht läuft gerade (auch aus einem geschlossenen Dialog). */
    readonly laeuft: boolean;
    /** Meldung des letzten Fehlschlags. */
    readonly fehler: string | null;
    /** Ereignisnummer des letzten Fehlschlags (siehe `pruefungBeginnt`). */
    readonly fehlerNr: number | null;
    /** Die gezielte Abfrage nach dem Fehler fand keinen Auftrag mit dieser ID. */
    readonly verwerfbar: boolean;
    /** Die gezielte Abfrage scheiterte (Netz o. Ä.) — beim nächsten Laden erneut. */
    readonly pruefungGescheitert: boolean;
    /**
     * Die Datei, mit der der Auftrag begann (Sendeauftrag; ein Nachdruck nutzt
     * die Datei des alten Auftrags). Scheiterte der erste Versuch vor dem
     * Hochladen, lädt die Wiederholung sonst ein anderes Blatt hoch als das,
     * das zur Abrechnung gemerkt ist.
     */
    readonly datei?: PlotDatei;
}

export interface PlotDatei {
    readonly blob: Blob;
    readonly breiteMm: number;
    readonly hoeheMm: number;
}

export const sendeSchluessel = (dokumentSchluessel: string) => `senden:${dokumentSchluessel}`;
export const nachdruckSchluessel = (altAuftragId: string) => `nachdruck:${altAuftragId}`;

let stand: ReadonlyMap<string, PlotSendeabsicht> = new Map();
let ereignisNr = 0;
const zuhoerer = new Set<() => void>();

const setzen = (neu: Map<string, PlotSendeabsicht>) => {
    stand = neu;
    for (const f of zuhoerer) f();
};

const gleicheId = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

const loeschen = (schluessel: string) => {
    const neu = new Map(stand);
    neu.delete(schluessel);
    setzen(neu);
};

/** Ruht die Absicht nach einem Fehler, der VOR dieser Prüfung lag? */
const pruefungZaehlt = (a: PlotSendeabsicht, pruefNr: number) =>
    !a.laeuft && a.fehlerNr !== null && pruefNr > a.fehlerNr;

/** Für `useSyncExternalStore`: dieselbe Map, bis sich etwas ändert. */
export function absichtenStand(): ReadonlyMap<string, PlotSendeabsicht> {
    return stand;
}

export function absichtenAbonnieren(f: () => void): () => void {
    zuhoerer.add(f);
    return () => { zuhoerer.delete(f); };
}

/**
 * Beginnt einen Sendeversuch: die vorhandene Absicht unverändert, sonst eine
 * neue aus `kopien` (und `datei`). `null`, wenn mit dieser Absicht schon ein
 * Aufruf läuft (Doppelklick, noch laufender Versuch aus einem geschlossenen
 * Dialog).
 */
export function absichtBeginnen(
    schluessel: string, plan: string, kopien: number, neueId: () => string, datei?: PlotDatei,
): PlotSendeabsicht | null {
    const alt = stand.get(schluessel);
    if (alt?.laeuft) return null;
    const absicht: PlotSendeabsicht = alt
        ? { ...alt, laeuft: true, fehler: null, verwerfbar: false, pruefungGescheitert: false }
        : { id: neueId(), kopien, plan, laeuft: true, fehler: null, fehlerNr: null, verwerfbar: false, pruefungGescheitert: false, datei };
    setzen(new Map(stand).set(schluessel, absicht));
    return absicht;
}

/**
 * Angekommen: Absicht löschen — nur, wenn noch dieselbe dasteht. `true`, wenn
 * dieser Aufruf sie gelöscht hat; hatte ein Listenabgleich sie schon aufgelöst,
 * `false` (wer auflöst, meldet den Auftrag — genau einmal).
 */
export function absichtErfolg(schluessel: string, id: string): boolean {
    const alt = stand.get(schluessel);
    if (!alt || !gleicheId(alt.id, id)) return false;
    loeschen(schluessel);
    return true;
}

/** Fehlgeschlagen oder ungewiss: Absicht bleibt, Verwerfen erst nach einer Prüfung. */
export function absichtFehler(schluessel: string, id: string, meldung: string): void {
    const alt = stand.get(schluessel);
    if (!alt || !gleicheId(alt.id, id)) return;
    ereignisNr += 1;
    setzen(new Map(stand).set(schluessel, {
        ...alt, laeuft: false, fehler: meldung, fehlerNr: ereignisNr, verwerfbar: false, pruefungGescheitert: false,
    }));
}

/**
 * Abgleich mit einer geladenen Auftragsliste des Dokuments: Absichten, deren ID
 * darin steht, sind angekommen und werden gelöscht — zurück kommen ihre
 * Schlüssel. Fehlen macht nichts verwerfbar (die Liste ist gekürzt).
 */
export function absichtenAbgleichen(plan: string, auftragIds: readonly string[]): string[] {
    const ids = new Set(auftragIds.map(id => id.toLowerCase()));
    const geloest = [...stand].filter(([, a]) => a.plan === plan && ids.has(a.id.toLowerCase())).map(([k]) => k);
    if (geloest.length > 0) {
        const neu = new Map(stand);
        for (const k of geloest) neu.delete(k);
        setzen(neu);
    }
    return geloest;
}

/** Gescheiterte, noch nicht geklärte Absichten des Dokuments — für die gezielte Prüfung. */
export function offeneFehlversuche(plan: string): [string, PlotSendeabsicht][] {
    return [...stand].filter(([, a]) => a.plan === plan && !a.laeuft && !a.verwerfbar && a.fehlerNr !== null);
}

/** Vor jeder gezielten Prüfung aufrufen; die Nummer geht an `absichtGeprueft`. */
export function pruefungBeginnt(): number {
    ereignisNr += 1;
    return ereignisNr;
}

/**
 * Ergebnis der gezielten Abfrage. Gefunden → angekommen, gelöscht (`true`) —
 * gleich, wann die Abfrage begann. Nicht gefunden → verwerfbar, aber nur, wenn
 * die Abfrage nach dem letzten Fehler begann und gerade kein Versuch läuft.
 */
export function absichtGeprueft(schluessel: string, id: string, pruefNr: number, gefunden: boolean): boolean {
    const alt = stand.get(schluessel);
    if (!alt || !gleicheId(alt.id, id)) return false;
    if (gefunden) {
        loeschen(schluessel);
        return true;
    }
    if (pruefungZaehlt(alt, pruefNr) && !alt.verwerfbar) {
        setzen(new Map(stand).set(schluessel, { ...alt, verwerfbar: true, pruefungGescheitert: false }));
    }
    return false;
}

/** Die gezielte Abfrage scheiterte: Verwerfen bleibt gesperrt, Hinweis an. */
export function absichtPruefungGescheitert(schluessel: string, id: string, pruefNr: number): void {
    const alt = stand.get(schluessel);
    if (!alt || !gleicheId(alt.id, id) || !pruefungZaehlt(alt, pruefNr) || alt.pruefungGescheitert) return;
    setzen(new Map(stand).set(schluessel, { ...alt, pruefungGescheitert: true }));
}

/** Verwerfen — nur eine ruhende, nachweislich nicht angekommene Absicht. */
export function absichtVerwerfen(schluessel: string, id: string): boolean {
    const alt = stand.get(schluessel);
    if (!alt || !gleicheId(alt.id, id) || alt.laeuft || !alt.verwerfbar) return false;
    loeschen(schluessel);
    return true;
}

/** Nur für Tests. */
export function absichtenLeeren(): void {
    ereignisNr = 0;
    setzen(new Map());
}
